import React, {useCallback} from "react";
import {Col, Row} from "reactstrap";
import Paper from "@mui/material/Paper";
import {ReactSortable} from "react-sortablejs";
import {CircularProgress, SvgIcon} from "@mui/material";
import ColorPickerPopover from "../../../../../../../components/app-specific/color-picker-popover";
import LazyImage from "../../../../../../../components/app-specific/lazy-image";
import LazySvg from "../../../../../../../components/app-specific/lazy-svg";
import {ReactComponent as HorizontalDragIcon} from "../../../../../../../../assets/images/notifications/horizontal-drag.svg";
import {ReactComponent as NoColorIcon} from "../../../../../../../../assets/images/notifications/no-color.svg";
import {ReactComponent as RemoveIcon} from "../../../../../../../../assets/images/remove.svg";
import IconButton from "../../../../../../../components/base/button/icon_button";

const PrintCatalogueViewConfigureGroupsSection = ({
                                                      configureGroupsState,
                                                      setConfigureGroupsState,
                                                      updatingStep,
                                                  }) => {

    /**
     * Reorders the categories.
     * @type {(function(*): void)}
     */
    const reorderCategories = useCallback((categories) => {
        setConfigureGroupsState(prevState => ({
            ...prevState,
            categories: categories,
        }))
    }, [setConfigureGroupsState])

    /**
     * Changes the category color.
     * @type {(function(*, *): void)}
     */
    const changeCategoryColor = useCallback((category, color) => {
        setConfigureGroupsState(prevState => ({
            ...prevState,
            categories: prevState.categories.map(cat => {
                if (cat.id === category.id) {
                    return {
                        ...cat,
                        colorCode: color,
                    }
                }
                return cat;
            })
        }))
    }, [setConfigureGroupsState])

    /**
     * Removes the category.
     * @type {(function(*): void)}
     */
    const removeCategory = useCallback((category) => {
        setConfigureGroupsState(prevState => ({
            ...prevState,
            categories: prevState.categories.filter(cat => cat.id !== category.id),
            products: prevState.products.filter(p => p.parentCategoryId !== category.id),
        }))
    }, [setConfigureGroupsState])

    /**
     * Reorders the makes.
     * @type {(function(*): void)}
     */
    const reorderMakes = useCallback((makes) => {
        setConfigureGroupsState(prevState => ({
            ...prevState,
            makes: makes,
        }))
    }, [setConfigureGroupsState])

    /**
     * Removes the Make.
     * @type {(function(*): void)}
     */
    const removeMake = useCallback((make) => {
        setConfigureGroupsState(prevState => ({
            ...prevState,
            makes: prevState.makes.filter(m => m.id !== make.id),
            products: prevState.products.filter(p => p.makeId !== make.id),
        }))
    }, [setConfigureGroupsState])

    /**
     * Reorders the display tags.
     * @type {(function(*): void)}
     */
    const reorderDisplayTags = useCallback((displayTags) => {
        setConfigureGroupsState(prevState => ({
            ...prevState,
            displayTags: displayTags,
        }))
    }, [setConfigureGroupsState])

    /**
     * Removes the Tag.
     * @type {(function(*): void)}
     */
    const removeTag = useCallback((tag) => {
        setConfigureGroupsState(prevState => ({
            ...prevState,
            tags: prevState.tags.filter(t => t.id !== tag.id),
        }))
    }, [setConfigureGroupsState])

    return (
        <>
            <Row className={'w-100 mb-5 pb-5'}>
                <Col xs={12}>
                    <p className={'mx-2 text-sm black mb-3'}>
                        The following categories, makes and display tags have been selected for the Catalogue. You can sort the order of the
                        categories, makes and display tags by dragging and dropping them.
                    </p>
                </Col>
                <Col xs={12} lg={6} xl={4} className={'mb-5'}>
                    <Paper className={'paper d-flex flex-column h-100 p-3'}>
                        <div className={'w-100 mb-4 d-flex align-items-center justify-content-between'}>
                            <p className={'text-md font-weight-semi-bold'}>
                                Categories
                            </p>
                        </div>
                        <ReactSortable
                            className={'flex-column w-100'}
                            list={configureGroupsState.categories}
                            setList={reorderCategories}
                            handle={'.handle'}
                            animation={210}
                            forceFallback={false}
                            swapThreshold={0.65}
                        >
                            {
                                configureGroupsState.categories.map(category => (
                                    <div key={category.id} className={'print-catalogue-category-card'}>
                                        <SvgIcon className={'handle'} component={HorizontalDragIcon}/>
                                        <ColorPickerPopover
                                            className={'color-circle-container'}
                                            color={category.colorCode}
                                            onImmediateChange={() => void 0}
                                            onSelect={(c) => changeCategoryColor(category, c)}
                                        >
                                            {(onEnter) =>
                                                <div className={'color-circle'} onClick={onEnter}>
                                                    <NoColorIcon className={'placeholder'}/>
                                                    <div className={'circle'} style={{'--color': category.colorCode}}/>
                                                </div>
                                            }
                                        </ColorPickerPopover>
                                        <p className={'title flex-grow-1'}>
                                            {
                                                category?.title?.concat((category.industryTitle ?? category.industry?.title) ? ` (${category.industryTitle ?? category.industry?.title})` : '')
                                            }
                                        </p>
                                        <IconButton
                                            onPressed={() => removeCategory(category)}
                                            disabled={updatingStep}
                                            color={'paper.error'}
                                            className={'remove-icon ml-auto'}
                                        >
                                            {
                                                updatingStep
                                                    ? <CircularProgress size={20} color={'secondary'}/>
                                                    : <SvgIcon component={RemoveIcon} className='icon normal'/>
                                            }
                                        </IconButton>
                                    </div>
                                ))
                            }
                        </ReactSortable>
                    </Paper>
                </Col>
                <Col xs={12} lg={6} xl={4} className={'mb-5'}>
                    <Paper className={'paper d-flex flex-column h-100 p-3'}>
                        <div className={'w-100 mb-4'}>
                            <p className={'text-md font-weight-semi-bold'}>
                                Makes
                            </p>
                        </div>
                        <ReactSortable
                            className={'flex-column w-100'}
                            list={configureGroupsState.makes}
                            setList={reorderMakes}
                            handle={'.handle'}
                            animation={210}
                            forceFallback={false}
                            swapThreshold={0.65}
                        >
                            {
                                configureGroupsState.makes.map(make => (
                                    <div key={make.id} className={'print-catalogue-make-card'}>
                                        <SvgIcon className={'handle'} component={HorizontalDragIcon}/>
                                        <p className={'title flex-grow-1'}>
                                            {make?.title ?? ''}
                                        </p>
                                        <IconButton
                                            onPressed={() => removeMake(make)}
                                            disabled={updatingStep}
                                            color={'paper.error'}
                                            className={'remove-icon ml-auto'}
                                        >
                                            {
                                                updatingStep
                                                    ? <CircularProgress size={20} color={'secondary'}/>
                                                    : <SvgIcon component={RemoveIcon} className='icon normal'/>
                                            }
                                        </IconButton>
                                    </div>
                                ))
                            }
                        </ReactSortable>
                    </Paper>
                </Col>
                <Col xs={12} lg={12} xl={4} className={'mb-5'}>
                    <Paper className={'paper d-flex flex-column h-100 p-3'}>
                        <div className={'w-100 mb-4'}>
                            <p className={'text-md font-weight-semi-bold'}>
                                Display Tags
                            </p>
                        </div>
                        <ReactSortable
                            className={'flex-column w-100'}
                            list={configureGroupsState.tags}
                            setList={reorderDisplayTags}
                            handle={'.handle'}
                            animation={210}
                            forceFallback={false}
                            swapThreshold={0.65}
                        >
                            {
                                configureGroupsState.tags.map(tag => (
                                    <div key={tag.id} className={'print-catalogue-tag-card'}>
                                        <SvgIcon className={'handle'} component={HorizontalDragIcon}/>
                                        <div className={'tag-container'}>
                                            <div className={'tag-icon-container'}>
                                                {
                                                    tag?.iconFileName && (
                                                        tag?.iconFileName?.toLowerCase()?.endsWith('.png')
                                                            ? <LazyImage
                                                                src={tag?.iconFileName}
                                                                height={20}
                                                                width={20}
                                                            />
                                                            : <LazySvg
                                                                src={tag?.iconFileName}
                                                                height={20}
                                                                width={20}
                                                            />
                                                    )
                                                }
                                            </div>
                                        </div>
                                        <p className={'title flex-grow-1 mr-auto'}>
                                            {tag?.name ?? ''}
                                        </p>
                                        <IconButton
                                            onPressed={() => removeTag(tag)}
                                            disabled={updatingStep}
                                            color={'paper.error'}
                                            className={'remove-icon ml-auto'}
                                        >
                                            {
                                                updatingStep
                                                    ? <CircularProgress size={20} color={'secondary'}/>
                                                    : <SvgIcon component={RemoveIcon} className='icon normal'/>
                                            }
                                        </IconButton>
                                    </div>
                                ))
                            }
                        </ReactSortable>
                        {
                            !configureGroupsState.tags.length && (
                                <p className={'text-sm mt-4'}>
                                    No display tags selected.
                                </p>
                            )
                        }
                    </Paper>
                </Col>
            </Row>
        </>
    );
}
export default PrintCatalogueViewConfigureGroupsSection;
